import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Title, Text, Card } from '@tremor/react';
import { colors } from '../../lib/colors';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { useEffect, useState } from 'react';
import type { CaseStudy } from '../../utils/caseStudyTypes';
import { getCaseStudyBySlug } from '../../utils/caseStudyLoader';
import { Link } from 'react-router-dom';

export default function IfaStrategy() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);
  const [caseStudySnippet, setCaseStudySnippet] = useState<CaseStudy | null>(null);
  const [caseStudyError, setCaseStudyError] = useState<string | null>(null);

  useEffect(() => {
    getCaseStudyBySlug('farm-succession-ifa-liquidity')
      .then((study) => {
        if (!study) {
          setCaseStudyError('Case study coming soon.');
          return;
        }
        setCaseStudySnippet(study);
      })
      .catch(() => {
        setCaseStudyError('Case study coming soon.');
      });
  }, []);

  return (
    <MarketingLayout>
      {/* Main Content Section */}
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation - all logic handled by the component */}
          <ServicePageNavigation currentSlug="immediate-financing-arrangement-ifa" />
          
          {/* Header */}
          <div className="text-center mb-12">
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Immediate Financing Arrangement</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Unlock capital trapped in your incorporated business or farm using a sophisticated strategy leveraging permanent life insurance.
            </Text>
          </div>

          <Card className="mb-8 p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
            <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is the Immediate Financing Arrangement (IFA)?</Title>
            <Text className="text-gray-600 dark:text-gray-300">
              The Immediate Financing Arrangement (IFA) is a powerful financial strategy primarily for established, incorporated agricultural and business owners in Saskatchewan and Alberta. It involves using a specially designed permanent life insurance policy (often participating whole life) as collateral to secure immediate financing from a third-party lender. This allows you to access significant capital—often tied up in illiquid assets like land or equipment—without needing to sell those core assets. The goal is to fund critical objectives like business expansion, succession planning, or investments while optimizing tax efficiency.
            </Text>
          </Card>

          <Card className="mb-8 p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
            <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How It Works: A Step-by-Step Guide</Title>
            {/* Consider adding a simple visual diagram here if possible */}
            <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
              <li><strong>Acquire Policy:</strong> Your corporation purchases a high-cash-value permanent life insurance policy (typically Whole Life) on a key shareholder or employee.</li>
              <li><strong>Assign Collateral:</strong> The policy's cash surrender value (CSV) is assigned as collateral to an approved lender (bank or specialized institution).</li>
              <li><strong>Secure Loan:</strong> Based on the collateral, the lender provides immediate financing, often structured as a line of credit, potentially up to 100% of the CSV or even the premiums paid.</li>
              <li><strong>Invest Funds:</strong> The borrowed capital is strategically deployed – reinvested into the business, used for acquisitions, funding buy-sell agreements, or other income-generating purposes.</li>
              <li><strong>Service Loan & Policy:</strong> Your corporation makes regular interest payments on the loan and continues paying the insurance policy premiums.</li>
              <li><strong>Repay Principal:</strong> The loan principal can be repaid during the insured's lifetime or, more commonly, is repaid from the tax-free death benefit upon the insured's passing, with the remaining benefit going to the designated beneficiaries (often the corporation or estate).</li>
            </ol>
          </Card>

          <Card className="mb-8 p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
            <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits for Your Farm/Business</Title>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2">
              <li><strong>Enhanced Liquidity:</strong> Access substantial capital immediately without selling farmland, equipment, or business shares, preserving operational continuity.</li>
              <li><strong>Tax Efficiency:</strong> Cash value within the policy grows tax-deferred. Loan interest <em>may</em> be tax-deductible if borrowed funds generate taxable income (requires careful structuring and professional advice). The death benefit is received tax-free by the corporation.</li>
              <li><strong>Capital Dividend Account (CDA) Creation:</strong> The death benefit payout (less the policy's Adjusted Cost Basis) creates a credit to the corporation's CDA, allowing tax-free dividends to be paid out to shareholders or the estate – a significant advantage for transferring corporate wealth.</li>
              <li><strong>Estate Enhancement & Succession:</strong> Provides tax-free funds to cover estate taxes, facilitate equitable distributions to heirs (especially non-farming children), or fund shareholder/buy-sell agreements smoothly.</li>
              <li><strong>Business Growth & Opportunities:</strong> Capital can fuel expansion, acquisitions, diversification, or other strategic investments.</li>
            </ul>
            <Text className="text-gray-600 dark:text-gray-300 mt-3">
              *Note: Potential tax savings, such as interest deductibility and CDA credits, are significant but depend on specific structuring and require professional tax advice.*
            </Text>
          </Card>

          {/* Case Study Section */}
          <Card className="mb-8 p-6 bg-white border border-gray-200 shadow-md">
            <Title className="text-2xl font-semibold text-gray-900 mb-3">
              {caseStudySnippet?.title || 'Case Study: Unlocking Farm Succession Liquidity'}
            </Title>
            {caseStudySnippet ? (
              <>
                <Text className="text-gray-700 leading-7">
                  {caseStudySnippet.summary}
                </Text>
                <Text className="text-gray-800 mt-3 font-medium">
                  Key moves that delivered the result
                </Text>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mt-3 pl-5">
                  {caseStudySnippet.implementationHighlights.slice(0, 3).map((item, index) => (
                    <li key={`ifa-highlight-${index}`}>{item}</li>
                  ))}
                </ul>
                {caseStudySnippet.metrics[0] && (
                  <Text className="text-gray-800 mt-4 font-semibold">
                    Headline result: {caseStudySnippet.metrics[0].value}
                  </Text>
                )}
                <div className="flex flex-wrap items-center gap-3 mt-5">
                  <button
                    onClick={() => setShowBookingWidget(true)}
                    className="px-4 py-2 rounded-md text-sm font-semibold text-white"
                    style={{ background: colors.brand.primary }}
                  >
                    Talk to an Advisor
                  </button>
                  <Link
                    className="text-sm font-medium text-blue-600 hover:text-blue-500"
                    to={`/case-studies#${caseStudySnippet.slug}`}
                  >
                    Read the full case study
                  </Link>
                </div>
                {caseStudySnippet.compliance && (
                  <Text className="text-xs text-gray-500 mt-4">
                    {caseStudySnippet.compliance}
                  </Text>
                )}
              </>
            ) : (
              <Text className="text-gray-700">
                {caseStudyError || 'We are refreshing this case study. Check back shortly for the full story.'}
              </Text>
            )}
          </Card>

          <Card className="mb-8 p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
            <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Understanding the Risks & Considerations</Title>
            <Text className="text-gray-600 dark:text-gray-300 mb-3">IFAs are complex and not without risk. Transparency is key:</Text>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2">
              <li><strong>Interest Rate Risk:</strong> Most IFA loans have floating rates. Increases in the prime rate will raise your borrowing costs.</li>
              <li><strong>Policy Performance:</strong> Dividends on participating policies are not guaranteed and can fluctuate, potentially affecting cash value growth compared to initial projections.</li>
              <li><strong>Lender Risk:</strong> Lenders can change criteria, may require annual reviews, and could demand more collateral or partial repayment if policy performance lags or rates rise significantly. Loan approval is not guaranteed.</li>
              <li><strong>Complexity & Costs:</strong> Requires coordinated advice (financial, legal, tax). Involves ongoing premium and interest payments, plus potential setup fees.</li>
              <li><strong>Reduced Death Benefit:</strong> The outstanding loan balance reduces the net amount paid to beneficiaries.</li>
              <li><strong>Tax Rule Changes:</strong> Future legislative changes could impact interest deductibility or CDA rules.</li>
              <li><strong>Early Termination:</strong> Surrendering the policy prematurely can trigger taxes if the cash value exceeds the adjusted cost basis.</li>
            </ul>
          </Card>

          <Card className="mb-8 p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
            <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is an IFA Right for Your Operation?</Title>
            <Text className="text-gray-600 dark:text-gray-300">
              This strategy is best suited for high-net-worth individuals with profitable, <strong>incorporated</strong> businesses or farms in Saskatchewan or Alberta. Key suitability factors include:
            </Text>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 mt-2">
              <li>A clear need for permanent life insurance.</li>
              <li>Substantial and stable corporate cash flow to comfortably cover ongoing premiums and loan interest payments (minimum annual premiums typically start around $200,000).</li>
              <li>Comfort with financial leverage and the associated risks.</li>
              <li>A long-term financial perspective.</li>
              <li>Ability to meet medical and financial underwriting requirements.</li>
            </ul>
            <Text className="text-gray-600 dark:text-gray-300 mt-3">
              PCM specializes in evaluating if an IFA aligns with your specific situation and goals, working alongside your legal and tax advisors.
            </Text>
          </Card>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Explore the IFA Strategy for Your Operation</Title>
            <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
              Is the Immediate Financing Arrangement the right tool to unlock capital and achieve your strategic goals? Let's discuss your specific situation.
            </Text>
            <button
              onClick={() => setShowBookingWidget(true)}
              className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
              style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
            >
              Discuss the IFA Strategy
            </button>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
         <BookingWidget onClose={() => setShowBookingWidget(false)} />
      )}
    </MarketingLayout>
  );
}
